import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { IContest, SubmissionCategory } from '../../../types/global/helper';
import { convertContestSubmissionMode } from '../../../redux/contest/actions';
import { useDispatchPromise, showToastMessage } from '../../../utils/AppUtils';
import {
    buildToRegularPayload,
    buildToTierPayload,
    pickDefaultTierId,
    resolveCategorySubmissionMode,
    seedTiersFromRegularCategory,
    tierFeeMajorUnits,
    validateTierWindows,
    type ContestTierFeeDraft,
} from '../../../utils/contestSubmissionMode';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../contestWizard/midnightStyles';

export type ConvertSubmissionModeDirection = 'to-regular' | 'to-tier';

interface Props {
    open: boolean;
    direction: ConvertSubmissionModeDirection;
    contest: IContest;
    category: SubmissionCategory;
    hasPaidOrAnySubmissions?: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

function datetimeLocalValue(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes(),
    )}`;
}

function isoFromDatetimeLocal(value: string): string | null {
    if (!value.trim()) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function BrandConvertSubmissionModeDialog({
    open,
    direction,
    contest,
    category,
    hasPaidOrAnySubmissions = false,
    onClose,
    onSuccess,
}: Props) {
    const dispatchPromise = useDispatchPromise();
    const [saving, setSaving] = useState(false);
    const [defaultTierId, setDefaultTierId] = useState('');
    const [force, setForce] = useState(false);
    const [keepInactive, setKeepInactive] = useState(true);
    const [tiers, setTiers] = useState<ContestTierFeeDraft[]>([]);

    const mode = resolveCategorySubmissionMode(category, contest);
    const tierOptions = useMemo(() => category.tier_fee ?? [], [category.tier_fee]);

    useEffect(() => {
        if (!open) return;
        setForce(false);
        setKeepInactive(true);
        setDefaultTierId(pickDefaultTierId(category) || '');
        if (direction === 'to-tier') {
            if (category.tier_fee?.length) {
                setTiers(
                    category.tier_fee.map(t => ({
                        _id: t._id,
                        label: t.label,
                        fee: tierFeeMajorUnits(t),
                        currency: t.currency || category.currency || 'USD',
                        starts_at: t.starts_at ?? null,
                        ends_at: t.ends_at ?? null,
                        timezone: t.timezone ?? contest.timezone ?? null,
                        is_active: t.is_active !== false,
                    })),
                );
            } else {
                setTiers(seedTiersFromRegularCategory(category));
            }
        }
    }, [open, direction, category, contest.timezone]);

    const selectedTier = useMemo(() => tierOptions.find(t => t._id === defaultTierId), [tierOptions, defaultTierId]);

    const canSubmit = useMemo(() => {
        if (saving) return false;
        if (direction === 'to-regular') return Boolean(defaultTierId);
        if (!tiers.length) return false;
        if (tiers.some(t => !t.label.trim())) return false;
        if (validateTierWindows(tiers)) return false;
        return true;
    }, [saving, direction, defaultTierId, tiers]);

    const updateTier = (index: number, patch: Partial<ContestTierFeeDraft>) => {
        setTiers(prev => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
    };

    const handleSubmit = async () => {
        if (!contest._id || !canSubmit) return;
        if (direction === 'to-regular' && hasPaidOrAnySubmissions && !force) {
            showToastMessage('Confirm convert with the force option when submissions already exist.', 'warning');
            return;
        }
        const windowError = direction === 'to-tier' ? validateTierWindows(tiers) : null;
        if (windowError) {
            showToastMessage(windowError, 'error');
            return;
        }

        setSaving(true);
        try {
            if (direction === 'to-regular') {
                await dispatchPromise(
                    convertContestSubmissionMode({
                        contestId: contest._id,
                        direction: 'to-regular',
                        body: buildToRegularPayload({
                            categoryId: category._id,
                            defaultTierId,
                            force: hasPaidOrAnySubmissions ? force : false,
                            keepInactiveTiers: keepInactive,
                        }),
                    }),
                );
            } else {
                const mapLabel = tiers[0]?.label?.trim() || 'Standard';
                await dispatchPromise(
                    convertContestSubmissionMode({
                        contestId: contest._id,
                        direction: 'to-tier',
                        body: buildToTierPayload({
                            categoryId: category._id,
                            tiers,
                            seedFromRegularFee: mode === 'regular',
                            mapRegularFeeToLabel: mapLabel,
                        }),
                    }),
                );
            }
            onSuccess?.();
            onClose();
        } catch {
            /* saga toasts */
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: midnight.surfaceLow,
                    backgroundImage: 'none',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px',
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    pb: 1,
                }}>
                <Box>
                    <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                        {direction === 'to-regular' ? 'Use a single fee (no tiers)' : 'Enable tiered pricing'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, mt: 0.5 }}>
                        {category.category_name}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={saving}
                    aria-label="Close"
                    sx={{ color: midnight.onSurfaceVariant, mt: -0.5 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 3 }}>
                {direction === 'to-regular' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography sx={{ fontSize: 14, color: midnight.onSurfaceVariant, lineHeight: 1.55 }}>
                            All <strong>new</strong> entries will use the selected tier&apos;s fee. Past entries keep
                            their original tier and amount.
                        </Typography>
                        <Box>
                            <Typography sx={editorialLabelSx}>Which tier becomes the flat fee?</Typography>
                            <TextField
                                select
                                fullWidth
                                value={defaultTierId}
                                onChange={e => setDefaultTierId(e.target.value)}
                                sx={editorialInputSx}>
                                {tierOptions.map(t => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {t.label} · {t.formatted_for_display || `$${tierFeeMajorUnits(t)}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        {selectedTier ? (
                            <Typography sx={{ fontSize: 13, color: midnight.onSurface }}>
                                New entries: <strong>{selectedTier.label}</strong> at{' '}
                                <strong>
                                    {selectedTier.formatted_for_display || `$${tierFeeMajorUnits(selectedTier)}`}
                                </strong>
                            </Typography>
                        ) : null}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={keepInactive}
                                    onChange={e => setKeepInactive(e.target.checked)}
                                    sx={{ color: midnight.onSurfaceVariant }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                                    Keep other tiers on file (recommended)
                                </Typography>
                            }
                        />
                        {hasPaidOrAnySubmissions ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={force}
                                        onChange={e => setForce(e.target.checked)}
                                        sx={{ color: midnight.primary }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                                        I understand submissions already exist — convert anyway
                                    </Typography>
                                }
                            />
                        ) : null}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography sx={{ fontSize: 14, color: midnight.onSurfaceVariant, lineHeight: 1.55 }}>
                            Define at least one tier. Optional date windows create Early Bird → Standard style pricing.
                        </Typography>
                        {tiers.map((tier, index) => (
                            <Box
                                key={tier._id || `draft-${index}`}
                                sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    bgcolor: midnight.surfaceContainer,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <TextField
                                        fullWidth
                                        label="Tier label"
                                        value={tier.label}
                                        onChange={e => updateTier(index, { label: e.target.value })}
                                        sx={editorialInputSx}
                                    />
                                    <TextField
                                        label="Fee ($)"
                                        value={tier.fee}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                                            updateTier(index, { fee: raw === '' ? 0 : parseFloat(raw) || 0 });
                                        }}
                                        sx={{ ...editorialInputSx, width: 120, flexShrink: 0 }}
                                    />
                                    <IconButton
                                        size="small"
                                        disabled={tiers.length <= 1}
                                        onClick={() => setTiers(prev => prev.filter((_, i) => i !== index))}
                                        sx={{ mt: 1 }}>
                                        <DeleteOutlineIcon sx={{ color: midnight.onSurfaceVariant }} />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                    <TextField
                                        label="Available from"
                                        type="datetime-local"
                                        value={datetimeLocalValue(tier.starts_at)}
                                        onChange={e =>
                                            updateTier(index, { starts_at: isoFromDatetimeLocal(e.target.value) })
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        sx={editorialInputSx}
                                    />
                                    <TextField
                                        label="Available until"
                                        type="datetime-local"
                                        value={datetimeLocalValue(tier.ends_at)}
                                        onChange={e =>
                                            updateTier(index, { ends_at: isoFromDatetimeLocal(e.target.value) })
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        sx={editorialInputSx}
                                    />
                                </Box>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() =>
                                setTiers(prev => [
                                    ...prev,
                                    {
                                        label: '',
                                        fee: 0,
                                        currency: category.currency || 'USD',
                                        starts_at: null,
                                        ends_at: null,
                                        is_active: true,
                                    },
                                ])
                            }
                            sx={{ alignSelf: 'flex-start', color: midnight.primary, fontWeight: 700 }}>
                            Add tier
                        </Button>
                        {validateTierWindows(tiers) ? (
                            <Typography sx={{ fontSize: 12, color: '#ff7351' }}>
                                {validateTierWindows(tiers)}
                            </Typography>
                        ) : null}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
                    <Button
                        onClick={onClose}
                        disabled={saving}
                        sx={{ color: midnight.onSurfaceVariant, fontWeight: 700 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canSubmit || (hasPaidOrAnySubmissions && direction === 'to-regular' && !force)}
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: midnight.primary,
                            color: '#392700',
                            fontWeight: 800,
                            px: 3,
                            '&:hover': { bgcolor: '#e6b55a' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.35)', color: 'rgba(57,39,0,0.5)' },
                        }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#392700' }} /> : 'Confirm'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
